import { Seo } from "./Seo";
import { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Sun, Moon } from "lucide-react";

export const LEGAL_LINKS = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-use", label: "Terms of Use" },
  { to: "/cookies-policy", label: "Cookie Policy" },
  { to: "/refund-policy", label: "Refund Policy" },
];

const LegalThemeContext = createContext(false);

const T = {
  dark: {
    page: "bg-[#05050A] text-slate-300",
    nav: "bg-[#05050A]/80 border-white/5",
    back: "text-slate-300 hover:text-white",
    toggle: "border-white/15 text-slate-200 hover:bg-white/5",
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.18),_rgba(5,5,10,0.9)_60%,_#05050A_100%)]",
    heroBorder: "border-white/5",
    overline: "text-[#00F0FF]",
    title: "text-white",
    updated: "text-slate-400",
    body: "text-slate-300",
    sectionHeading: "text-white",
    contactCard: "border-[#007AFF]/25 bg-[#0D0E15]",
    contactIcon: "bg-[#00F0FF]/10 text-[#00F0FF]",
    contactText: "text-slate-300",
    link: "text-[#00F0FF]",
    footerBorder: "border-white/5",
    footerLink: "text-slate-400 hover:text-white",
    footerMuted: "text-slate-500",
    marker: "marker:text-[#00F0FF]",
  },
  light: {
    page: "bg-[#F6F8FC] text-slate-700",
    nav: "bg-white/80 border-slate-200",
    back: "text-slate-600 hover:text-slate-900",
    toggle: "border-slate-300 text-slate-600 hover:bg-slate-100",
    heroGlow: "bg-[radial-gradient(ellipse_at_top,_rgba(0,122,255,0.12),_rgba(246,248,252,0.9)_60%,_#F6F8FC_100%)]",
    heroBorder: "border-slate-200",
    overline: "text-[#007AFF]",
    title: "text-slate-900",
    updated: "text-slate-500",
    body: "text-slate-700",
    sectionHeading: "text-slate-900",
    contactCard: "border-slate-200 bg-white",
    contactIcon: "bg-[#007AFF]/10 text-[#007AFF]",
    contactText: "text-slate-700",
    link: "text-[#007AFF]",
    footerBorder: "border-slate-200",
    footerLink: "text-slate-500 hover:text-slate-900",
    footerMuted: "text-slate-400",
    marker: "marker:text-[#007AFF]",
  },
};

export function LegalLayout({ title, updated, children }) {
  const [dark, setDark] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("pnr_home_theme") : null;
    return saved ? saved === "dark" : false;
  });
  useEffect(() => {
    localStorage.setItem("pnr_home_theme", dark ? "dark" : "light");
  }, [dark]);
  const t = dark ? T.dark : T.light;

  return (
    <LegalThemeContext.Provider value={dark}>
      <Seo title={title} path={typeof window !== "undefined" ? window.location.pathname : "/"} description={`${title} for PNR Decode — how we handle your data, your rights, and the terms of using our GDS itinerary conversion service.`} />
      <div className={`min-h-screen transition-colors duration-300 ${t.page}`} data-theme={dark ? "dark" : "light"}>
        <header className={`sticky top-0 z-30 backdrop-blur-xl border-b ${t.nav}`}>
          <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center" data-testid="legal-nav-brand">
              <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-9 w-auto object-contain" />
            </Link>
            <div className="flex items-center gap-3">
              <button
                data-testid="legal-theme-toggle"
                onClick={() => setDark((v) => !v)}
                aria-label={dark ? "Switch to light theme" : "Switch to dark theme"}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition-colors ${t.toggle}`}
              >
                {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <Link to="/" className={`inline-flex items-center gap-1.5 text-sm transition-colors ${t.back}`} data-testid="legal-back-home">
                <ArrowLeft className="h-4 w-4" /> Back to home
              </Link>
            </div>
          </div>
        </header>

        <div className={`relative overflow-hidden border-b ${t.heroBorder}`}>
          <div className={`absolute inset-0 ${t.heroGlow}`} />
          <div className="relative max-w-4xl mx-auto px-6 py-16">
            <div className={`text-xs font-bold uppercase tracking-[0.2em] ${t.overline}`}>PNR Decode — Legal</div>
            <h1 className={`mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight ${t.title}`} data-testid="legal-title">{title}</h1>
            {updated && <p className={`mt-4 text-sm ${t.updated}`} data-testid="legal-updated">Last updated: {updated}</p>}
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-6 py-12">
          <article className={`space-y-8 ${t.body}`}>{children}</article>
          <div className={`mt-14 rounded-2xl border p-6 flex items-center gap-4 ${t.contactCard}`}>
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg ${t.contactIcon}`}>
              <Mail className="h-5 w-5" />
            </span>
            <p className={`text-sm ${t.contactText}`}>
              Questions about this policy? Email us at{" "}
              <a href="mailto:support@pnrdecode.com" className={`${t.link} hover:underline font-medium`}>support@pnrdecode.com</a>.
            </p>
          </div>
        </main>

        <footer className={`border-t px-6 py-12 ${t.footerBorder}`}>
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <img src="/pnr-decode-wordmark.png" alt="PNR Decode" className="h-8 w-auto object-contain" />
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
              {LEGAL_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className={`transition-colors ${t.footerLink}`}>{l.label}</Link>
              ))}
            </div>
            <div className={`text-xs ${t.footerMuted}`}>© {new Date().getFullYear()} PNR Decode.</div>
          </div>
        </footer>
      </div>
    </LegalThemeContext.Provider>
  );
}

export function Section({ heading, children }) {
  const dark = useContext(LegalThemeContext);
  const t = dark ? T.dark : T.light;
  return (
    <section>
      <h2 className={`font-heading text-2xl font-bold mb-3 ${t.sectionHeading}`}>{heading}</h2>
      <div className={`space-y-3 leading-relaxed ${t.body}`}>{children}</div>
    </section>
  );
}

export function Li({ children }) {
  const dark = useContext(LegalThemeContext);
  const t = dark ? T.dark : T.light;
  return <li className={`ml-5 list-disc ${t.marker}`}>{children}</li>;
}

export function B({ children }) {
  const dark = useContext(LegalThemeContext);
  const t = dark ? T.dark : T.light;
  return <b className={t.sectionHeading}>{children}</b>;
}
